import { useEffect } from 'react';
import type { RefObject } from 'react';

/** Fields where a bare keystroke is text the visitor is typing, not a shortcut. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * Focus the search box when `/` is pressed.
 *
 * A directory lives on its search, so it should be reachable without hunting
 * for it. The handler stands down while the visitor is typing anywhere else,
 * and ignores any keystroke carrying a modifier so browser and OS shortcuts
 * keep working.
 */
export function useSearchShortcut(ref: RefObject<HTMLInputElement>): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/') return;
      // Auto-repeat: the first press focuses and selects the box, so every
      // repeat afterwards lands inside it and would overwrite the query with a
      // literal "/".
      if (event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      event.preventDefault();
      ref.current?.focus();
      ref.current?.select();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [ref]);
}
